import React from 'react';
import { X, Pencil, Trash2, Mail, Phone, Sun, Cloud, Plus } from 'lucide-react';
import './WorkerProfilePanel.css';

// ── Static data for this demo worker (David Chen) ────────────────────────────

interface WorkerTypeEntry {
    readonly role: string;
    readonly baseRate: string;  // shown when not edited
}

const WORKER_TYPES: readonly WorkerTypeEntry[] = [
    { role: 'Physiotherapist',     baseRate: '$78/hr' },
    { role: 'PT – Initial Assess', baseRate: '$45/hr' },
    { role: 'PT Overtime',         baseRate: '$117/hr' },
];

interface DeductionEntry {
    readonly name: string;
    readonly amount: string;
}

const PRESET_DEDUCTIONS: readonly DeductionEntry[] = [
    { name: 'Parking',       amount: '–$12 / shift' },
    { name: 'Meal Allowance', amount: '–$25 / day' },
];

const TRAINED_LOCATIONS: readonly { readonly name: string; readonly sub: string }[] = [
    { name: 'Kingston Health SC',  sub: 'Kingston Health Sciences Centre' },
    { name: 'Ottawa Hospital',     sub: 'Ottawa Hospital – Civic Campus' },
];

// ── Props interface ──────────────────────────────────────────────────────────

interface WorkerProfilePanelProps {
    readonly isVisible: boolean;
    readonly wpIsEditingRate: boolean;
    readonly wpEditRateText: string;
    readonly wpFirstRateDisplay: string;  // '$78/hr' → '$85/hr' after save
    readonly wpDeductionCount: number;    // 0, 1 or 2
}

// ── Component ────────────────────────────────────────────────────────────────

const WorkerProfilePanel = ({
    isVisible,
    wpIsEditingRate,
    wpEditRateText,
    wpFirstRateDisplay,
    wpDeductionCount,
}: WorkerProfilePanelProps): React.ReactElement => {

    const visibleClass: string = isVisible ? '--visible' : '';
    const rateWasUpdated: boolean = wpFirstRateDisplay !== '$78/hr';

    return (
        <>
            {/* Panel */}
            <div className={`wpp-panel${isVisible ? ' wpp-panel--visible' : ''}`}>

                {/* ── Header ─────────────────────────────────────────────── */}
                <div className='wpp-header'>
                    <span className='wpp-header-title'>Worker Profile</span>
                    <button className='wpp-close-btn' data-demo-target='wp-close-btn'>
                        <X size={11} />
                    </button>
                </div>

                {/* ── Worker identity ────────────────────────────────────── */}
                <div className='wpp-identity'>
                    <div className='wpp-avatar'>DC</div>

                    <div className='wpp-identity-info'>
                        <div className='wpp-name-row'>
                            <span className='wpp-name'>David Chen</span>
                            <Pencil size={9} style={{ color: '#9ca3af' }} />
                        </div>
                        <div className='wpp-availability'>
                            <span className='wpp-avail-chip'>
                                <Sun size={8} /> Morning
                            </span>
                            <span className='wpp-avail-chip'>
                                <Cloud size={8} /> Afternoon
                            </span>
                        </div>
                    </div>

                    <div className='wpp-status-area'>
                        <span className='wpp-status-badge'>Active</span>
                        <button className='wpp-change-status-btn'>Change Status</button>
                    </div>
                </div>

                {/* ── Contact cards ──────────────────────────────────────── */}
                <div className='wpp-contact-cards'>
                    <div className='wpp-contact-card'>
                        <div className='wpp-contact-label'><Mail size={9} /> Email</div>
                        <div className='wpp-contact-value'>d.chen@worky.com</div>
                    </div>
                    <div className='wpp-contact-card'>
                        <div className='wpp-contact-label'><Phone size={9} /> Phone</div>
                        <div className='wpp-contact-value'>+1 647-555-0183</div>
                    </div>
                </div>

                {/* ── Contractor Details ─────────────────────────────────── */}
                <div className='wpp-section'>
                    <div className='wpp-section-title-row'>
                        <span className='wpp-section-title'>Contractor Details</span>
                        <button className='wpp-section-action'><Plus size={9} /> Create</button>
                    </div>

                    <div className='wpp-subsection-title'>Current Worker Types</div>

                    {/* Row 0 — Physiotherapist (editable in animation) */}
                    {wpIsEditingRate ? (
                        <div className='wpp-worker-type-row wpp-worker-type-row--editing'>
                            <div className='wpp-edit-row'>
                                <span className='wpp-edit-role'>Physiotherapist</span>

                                <div className='wpp-rate-input-display' data-demo-target='wp-rate-input'>
                                    <span className='wpp-rate-prefix'>$</span>
                                    <span className='wpp-rate-typed'>{wpEditRateText}</span>
                                    <span className='wpp-rate-cursor' />
                                    <span className='wpp-rate-suffix'>/hr</span>
                                </div>

                                <button className='wpp-save-rate-btn' data-demo-target='wp-save-rate-btn'>
                                    Save
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className='wpp-worker-type-row'>
                            <div className='wpp-wt-info'>
                                <span className='wpp-wt-name'>Physiotherapist</span>
                                <span className={`wpp-wt-rate${rateWasUpdated ? ' wpp-wt-rate--updated' : ''}`}>
                                    {wpFirstRateDisplay}
                                </span>
                            </div>
                            <div className='wpp-wt-actions'>
                                <button
                                    className='wpp-icon-btn'
                                    data-demo-target='wp-edit-btn-physio'
                                >
                                    <Pencil size={10} />
                                </button>
                                <button className='wpp-icon-btn wpp-icon-btn--delete'>
                                    <Trash2 size={10} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Rows 1 & 2 — static types */}
                    {WORKER_TYPES.slice(1).map((wt: WorkerTypeEntry) => (
                        <div key={wt.role} className='wpp-worker-type-row'>
                            <div className='wpp-wt-info'>
                                <span className='wpp-wt-name'>{wt.role}</span>
                                <span className='wpp-wt-rate'>{wt.baseRate}</span>
                            </div>
                            <div className='wpp-wt-actions'>
                                <button className='wpp-icon-btn'><Pencil size={10} /></button>
                                <button className='wpp-icon-btn wpp-icon-btn--delete'><Trash2 size={10} /></button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Employment Deduction Configs ───────────────────────── */}
                <div className='wpp-section'>
                    <div className='wpp-section-title-row'>
                        <span className='wpp-section-title'>Employment Deduction Configs</span>
                        <button
                            className='wpp-section-action'
                            data-demo-target='wp-add-deduction-btn'
                        >
                            <Plus size={9} /> Add
                        </button>
                    </div>

                    <div className='wpp-deductions-list'>
                        {PRESET_DEDUCTIONS.slice(0, wpDeductionCount).map((d: DeductionEntry) => (
                            <div key={d.name} className='wpp-deduction-row'>
                                <span className='wpp-deduction-name'>{d.name}</span>
                                <span className='wpp-deduction-amount'>{d.amount}</span>
                            </div>
                        ))}
                    </div>

                    <div className='wpp-classification'>
                        ONTARIO · CONTRACTOR · Independent Contractor
                    </div>

                    <div className='wpp-pay-stub-row'>
                        <input
                            className='wpp-pay-stub-input'
                            placeholder='Add pay stub note'
                            readOnly
                        />
                        <button className='wpp-pay-stub-save'>SAVE</button>
                    </div>
                </div>

                {/* ── Trained Locations ──────────────────────────────────── */}
                <div className='wpp-section'>
                    <div className='wpp-section-title-row'>
                        <span className='wpp-section-title'>Trained Locations</span>
                    </div>
                    {TRAINED_LOCATIONS.map((loc: { readonly name: string; readonly sub: string }) => (
                        <div key={loc.name} className='wpp-trained-row'>
                            <div className='wpp-trained-info'>
                                <span className='wpp-trained-name'>{loc.name}</span>
                                <span className='wpp-trained-sub'>{loc.sub}</span>
                            </div>
                            <span className='wpp-trained-badge'>Trained</span>
                        </div>
                    ))}
                </div>

                {/* ── CTA ────────────────────────────────────────────────── */}
                <div className='wpp-cta-section'>
                    <button className='wpp-cta-btn'>
                        Add worker as pre-trained to client facility
                    </button>
                </div>

            </div>
        </>
    );
};

export { WorkerProfilePanel };
export type { WorkerProfilePanelProps };
